import { Link } from 'react-router-dom';
import { useLanguage } from '../../core/hooks/use-language';
import { useTranslation } from '../../core/hooks/use-translation';
import { HeroBackdrop } from './hero-backdrop';
import { HeroCarousel } from './hero-carousel';
import { HomeTopbar } from './home-topbar';

/**
 * The hero, which on this page is one container holding the background, the navigation and the
 * first screen of content — the arrangement the live static site uses so the bar can be
 * transparent over the gradient.
 *
 * The three figures are literals rather than string-table entries: `5+`, `7.0k+` and `98%` read the
 * same in both languages, and the styling splits the number from its suffix.
 *
 * **The posts link is here and not in the navigation above it.** That row needs 1199px in Russian
 * and 1258px in Kazakh before the drawer takes over at 768px, so a sixth entry would crowd it on
 * ordinary 1280px and 1366px laptop screens. Beside the call to action it is on
 * the first screen at every width. It has no border, so the booking button stays the one bordered
 * action in the hero and this reads as the second choice rather than a rival to it.
 */
export function HeroSection() {
    const { t } = useTranslation();
    const language = useLanguage();

    return (
        <div className="hero" id="home">
            <HeroBackdrop />
            <HomeTopbar />

            <main className="hero-main">
                <div className="hero-layout">
                    <div className="hero-content">
                        <h1 className="hero-title">{t('site.hero.title')}</h1>
                        <p className="hero-desc">{t('site.hero.desc')}</p>
                        <div className="hero-actions">
                            <Link to={`/${language}/book`} className="hero-readmore">
                                {t('site.hero.cta')}
                            </Link>
                            <Link to={`/${language}/blog`} className="hero-posts">
                                {t('site.hero.posts')}
                                <ArrowIcon />
                            </Link>
                        </div>
                        <div className="hero-stats">
                            <div>
                                <div className="hero-stat-value">
                                    5<span>+</span>
                                </div>
                                <div className="hero-stat-label">{t('site.hero.stat1')}</div>
                            </div>
                            <div>
                                <div className="hero-stat-value">
                                    7.0k<span>+</span>
                                </div>
                                <div className="hero-stat-label">{t('site.hero.stat2')}</div>
                            </div>
                            <div>
                                <div className="hero-stat-value">
                                    98<span>%</span>
                                </div>
                                <div className="hero-stat-label">{t('site.hero.stat3')}</div>
                            </div>
                        </div>
                    </div>
                    <div className="hero-visual">
                        <HeroCarousel />
                    </div>
                </div>
            </main>
        </div>
    );
}

/** `aria-hidden` because it sits beside its own label; announcing it would say the word twice. */
function ArrowIcon() {
    return (
        <svg
            className="hero-posts-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
    );
}
